import { KeyboardEvent, PointerEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SocialTopicMetric } from "@digital-twins/shared-types";

import { pickCategoricalChartPalette } from "@/lib/charts/chartjs";
import { SocialPlatformIcon } from "../SocialPlatformIcon";
import { SocialMeasureSwitch } from "../SocialStatRow";
import { SOCIAL_PANEL_BOTTOM_PADDING } from "../SocialTopicPanelHeader";
import { SOCIAL_PLATFORM_LABELS, formatSocialCount } from "../socialFormat";
import { useTopicHighlight } from "../SocialTopicHighlightContext";
import { SocialTopicSlotContext } from "../SocialTopicView";
import { socialTopicDayWindow, socialTopicWindowDays } from "../useSocialTopicFilters";
import { useSocialTopicSeries } from "../useSocialTopicSeries";
import { SocialChartTooltip, SocialChartTooltipRow } from "./SocialChartTooltip";
import {
  SOCIAL_BRAND_FALLBACK_COLOR,
  SOCIAL_PLATFORM_TEXT_CLASS,
  socialBrandColors,
  socialPlatformColorStyle,
} from "./socialChartColors";
import { socialTimelineSeriesQuery, SocialPeakPopover } from "./SocialPeakPopover";
import {
  brandLines,
  dailyNet,
  findPeaks,
  monotonePath,
  SocialFilledBucket,
  stackLayers,
} from "./socialTopicMetrics";
import { formatBucketDate, pointX, useChartWidth } from "./svgScale";

interface SocialTimelineChartProps {
  /** Reads the day series; the brush sets `window` on `context.topicFilters` (UTC day bounds). */
  context: SocialTopicSlotContext;
}

/** What the chart splits the measure by: stacked channels, or one line per brand. */
export type SocialTimelineSplit = "channel" | "brand";
const TIMELINE_SPLITS: ReadonlyArray<SocialTimelineSplit> = ["channel", "brand"];

/** Brand lines shown by default, largest first; the rest wait in the legend. */
export const TIMELINE_TOP_BRANDS = 5;

/** Stacked areas draw their series color at this opacity; legend and tooltip swatches are solid. */
export const TIMELINE_AREA_OPACITY = 0.55;

const PAD_LEFT = 40;
const PAD_TOP = 34;
const PAD_BOTTOM = 36;
const NARROW_WIDTH = 600;
const RIBBON_HEIGHT = 10;
// The ribbon ends the svg; the card padding under it is the shared panel padding.
const RIBBON_FROM_BOTTOM = RIBBON_HEIGHT;

/**
 * One `[left, right]` span per bucket, the bucket centred on its point and
 * clipped to the plot's x range `[x0, x1]`, so the first and last spans stop
 * at the plot edges instead of half a bucket past them.
 */
export function bucketSpan(
  index: number,
  count: number,
  x0: number,
  x1: number,
): [number, number] {
  const center = pointX(index, count, x0, x1);
  const half = count > 1 ? (x1 - x0) / (count - 1) / 2 : (x1 - x0) / 2;
  return [Math.max(x0, center - half), Math.min(x1, center + half)];
}

/** The axis top: 2, 5 or 10 times a power of ten at or above `value`, at least 4. */
export function timelineAxisMax(value: number): number {
  if (!Number.isFinite(value) || value <= 4) {
    return 4;
  }
  const power = 10 ** Math.floor(Math.log10(value));
  const normalized = value / power;
  return (normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * power;
}

/** The mood ribbon cell's fill: hue only for a positive or negative day, track grey without a sentiment. */
export function moodFill(net: number | null): string {
  if (net == null) {
    return "hsl(var(--chart-bar-track) / 0.5)";
  }
  if (net === 0) {
    return "hsl(var(--foreground) / 0.15)";
  }
  const alpha = (0.25 + (0.75 * Math.min(100, Math.abs(net))) / 100).toFixed(2);
  return net > 0 ? `hsl(var(--chart-positive) / ${alpha})` : `hsl(var(--chart-negative) / ${alpha})`;
}

/** An area between two monotone curves: `top` left to right, back along `bottom`. */
function bandPath(top: Array<[number, number]>, bottom: Array<[number, number]>): string {
  const reversed = bottom.slice().reverse();
  const back = monotonePath(reversed);
  const backCurves = back.indexOf(" C") >= 0 ? back.slice(back.indexOf(" C")) : "";
  const [bx, by] = reversed[0];
  return `${monotonePath(top)} L${bx.toFixed(1)},${by.toFixed(1)}${backCurves} Z`;
}

function signed(value: number): string {
  return value > 0 ? `+${value}` : String(value);
}

interface Drag {
  from: number;
  to: number;
}

/**
 * Reach (or posts) over time: stacked channel tints, the total line, the
 * mood ribbon, the brush and the peaks. Reads every filter but the window,
 * so the brush moves across the whole span and veils what it leaves out.
 */
export function SocialTimelineChart(props: SocialTimelineChartProps) {
  const { context } = props;
  const { universe, topicFilters, refreshKey } = context;

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [openPeak, setOpenPeak] = useState<string | null>(null);
  const [split, setSplit] = useState<SocialTimelineSplit>("channel");
  // A legend click's choice per brand; a brand without one shows when it is in the top 5.
  const [brandChoices, setBrandChoices] = useState<Record<string, boolean>>({});

  const { t, i18n } = useTranslation("app");
  const { setActive } = useTopicHighlight();
  const plotRef = useRef<HTMLDivElement>(null);
  const width = Math.max(280, useChartWidth(plotRef));
  const hasBrands = universe.brands.length > 0;
  const isBrandSplit = split === "brand" && hasBrands;
  const seriesSplit = isBrandSplit ? "brand" : undefined;
  const seriesQuery = useMemo(
    () => socialTimelineSeriesQuery(topicFilters, seriesSplit),
    [topicFilters, seriesSplit],
  );
  const { series, buckets, isLoading } = useSocialTopicSeries(
    universe.token,
    seriesQuery,
    refreshKey,
  );
  const metric: SocialTopicMetric = topicFilters.filters.metric;
  const stack = useMemo(() => stackLayers(buckets, metric), [buckets, metric]);
  const nets = useMemo(() => dailyNet(buckets), [buckets]);
  const peaks = useMemo(() => findPeaks(stack.totals, metric), [stack, metric]);
  // The app's categorical chart palette (theme and org palette aware), in order, largest channel first.
  const seriesColors = useMemo(
    () => pickCategoricalChartPalette(stack.order.length),
    [stack.order.length],
  );

  const days = useMemo(() => buckets.map((entry) => entry.start), [buckets]);
  const n = days.length;
  const brandColors = useMemo(() => socialBrandColors(universe.brands), [universe.brands]);
  const lines = useMemo(
    () =>
      brandLines(
        series?.brands ?? [],
        days,
        metric,
        universe.brands.map((brand) => brand.id),
      ),
    [series, days, metric, universe.brands],
  );
  const isLineShown = (brandId: string, rank: number) =>
    brandChoices[brandId] ?? rank < TIMELINE_TOP_BRANDS;
  const shownLines = lines.filter((line, rank) => isLineShown(line.brandId, rank));
  const brandColor = (brandId: string) => brandColors.get(brandId) ?? SOCIAL_BRAND_FALLBACK_COLOR;
  const brandName = (brandId: string) =>
    context.brandNames.get(brandId) ??
    universe.brands.find((brand) => brand.id === brandId)?.name ??
    brandId;
  /** The day's highest shown brand value: where a peak marker sits in brand mode. */
  const shownMaxAt = (index: number) =>
    Math.max(0, ...shownLines.map((line) => line.values[index] ?? 0));
  // Short enough that chart and side column share one row of about 320 px.
  const height = width < NARROW_WIDTH ? 200 : 240;
  // No post has a sentiment: the mood ribbon and its label would be an empty grey track.
  const hasMood = nets.some((net) => net != null);
  const plotBottom = height - PAD_BOTTOM;
  const dayWidth = (width - PAD_LEFT) / Math.max(1, n - 1);
  const x = (index: number) => pointX(index, n, PAD_LEFT, width);
  const span = (index: number) => bucketSpan(index, n, PAD_LEFT, width);
  const axisMax = timelineAxisMax(
    Math.max(1, ...(isBrandSplit ? shownLines.flatMap((line) => line.values) : stack.totals)),
  );
  const y = (value: number) => PAD_TOP + (plotBottom - PAD_TOP) * (1 - value / axisMax);
  const locale = i18n.language;
  const formatCount = (value: number) => formatSocialCount(value, locale);
  const dates = days.map((day) => formatBucketDate(day, locale));
  const windowDays = socialTopicWindowDays(topicFilters.filters.window);

  const indexAt = (clientX: number, target: Element): number => {
    const svg = target.closest("svg") ?? target;
    const rect = svg.getBoundingClientRect();
    const scale = rect.width > 0 ? width / rect.width : 1;
    const px = ((Number.isFinite(clientX) ? clientX : rect.left) - rect.left) * scale;
    const index = Math.round((px - PAD_LEFT) / dayWidth);
    return Math.max(0, Math.min(n - 1, n > 1 ? index : 0));
  };

  const onHover = (index: number | null) => {
    // Only a new day publishes the highlight, so moving within a day re-renders nothing else.
    if (index === hoverIndex) {
      return;
    }
    setHoverIndex(index);
    setActive(index == null ? null : { kind: "day", day: days[index] });
  };

  const applyRange = (a: number, b: number) => {
    const from = days[Math.min(a, b)];
    const to = days[Math.max(a, b)];
    if (windowDays && windowDays.from === from && windowDays.to === to) {
      topicFilters.setFilter("window", null);
      return;
    }
    topicFilters.setFilter("window", socialTopicDayWindow(from, to));
  };

  const onPointerDown = (event: PointerEvent<SVGRectElement>) => {
    const index = indexAt(event.clientX, event.currentTarget);
    if (typeof event.currentTarget.setPointerCapture === "function") {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // A synthetic pointer has no capture; the drag still ends on pointerup.
      }
    }
    setDrag({ from: index, to: index });
  };

  const onPointerMove = (event: PointerEvent<SVGRectElement>) => {
    const index = indexAt(event.clientX, event.currentTarget);
    onHover(index);
    if (drag) {
      setDrag({ from: drag.from, to: index });
    }
  };

  const onPointerUp = (event: PointerEvent<SVGRectElement>) => {
    if (!drag) {
      return;
    }
    const index = indexAt(event.clientX, event.currentTarget);
    setDrag(null);
    applyRange(drag.from, index);
  };

  const onPointerLeave = () => {
    if (!drag) {
      onHover(null);
    }
  };

  const onDayKeyDown = (event: KeyboardEvent<HTMLLIElement>, index: number) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      applyRange(index, index);
    }
  };

  const onPeakKeyDown = (event: KeyboardEvent<SVGGElement>, letter: string) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpenPeak(letter);
    }
  };

  const title = t(`socialListening.topicPage.timeline.title.${metric}`);
  const coveredPosts = buckets.reduce((sum, entry) => sum + entry.total.viewsPosts, 0);
  const totalPosts = buckets.reduce((sum, entry) => sum + entry.total.posts, 0);
  const hint = windowDays
    ? t("socialListening.topicPage.timeline.clearHint")
    : t("socialListening.topicPage.timeline.dragHint");
  const legend = (
    <div
      className="flex flex-wrap gap-3 px-5 pt-2 text-xs text-muted-foreground"
      data-testid="topic-timeline-legend"
    >
      {isBrandSplit
        ? lines.map((line, rank) => {
            const isShown = isLineShown(line.brandId, rank);
            return (
              <button
                key={line.brandId}
                type="button"
                aria-pressed={isShown}
                data-testid={`topic-timeline-legend-brand-${line.brandId}`}
                onClick={() =>
                  setBrandChoices((choices) => ({ ...choices, [line.brandId]: !isShown }))
                }
                className={
                  "inline-flex items-center gap-[5px] rounded-sm transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                  (isShown ? "text-foreground" : "opacity-60 hover:opacity-100")
                }
              >
                <i
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 flex-none rounded-[3px] border-2"
                  style={{
                    borderColor: brandColor(line.brandId),
                    background: isShown ? brandColor(line.brandId) : "transparent",
                  }}
                />
                {brandName(line.brandId)}
              </button>
            );
          })
        : stack.order.map((platform, rank) => (
        <span key={platform} className="inline-flex items-center gap-[5px]">
          <i
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 flex-none rounded-[3px]"
            style={{ background: seriesColors[rank] }}
          />
          <span className={SOCIAL_PLATFORM_TEXT_CLASS} style={socialPlatformColorStyle(platform)}>
            <SocialPlatformIcon platform={platform} className="h-3 w-3" />
          </span>
          {SOCIAL_PLATFORM_LABELS[platform]}
        </span>
      ))}
      {metric === "reach" ? (
        <>
          <span className="sr-only" data-testid="topic-timeline-caption">
            {t("socialListening.topicPage.timeline.caption")}
          </span>
          {coveredPosts < totalPosts ? (
            <span className="tabular-nums" data-testid="topic-timeline-coverage">
              {t("socialListening.topicPage.stats.reachCoverage", {
                covered: coveredPosts,
                count: totalPosts,
              })}
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );

  const valueLabel = (value: number) =>
    metric === "reach"
      ? t("socialListening.topicPage.timeline.views", { value: formatCount(value) })
      : t("socialListening.topicPage.timeline.posts", { count: value });

  const brushBand = (from: number, to: number, testId: string) => {
    const left = span(from)[0];
    const right = span(to)[1];
    return (
      <rect
        data-testid={testId}
        x={left}
        y={PAD_TOP}
        width={Math.max(1, right - left)}
        height={plotBottom - PAD_TOP}
        rx={4}
        fill="hsl(var(--primary) / 0.1)"
        stroke="hsl(var(--primary) / 0.55)"
        strokeWidth={1}
        pointerEvents="none"
      />
    );
  };

  const renderBrush = () => {
    if (drag) {
      return brushBand(Math.min(drag.from, drag.to), Math.max(drag.from, drag.to), "topic-timeline-brush-live");
    }
    if (!windowDays) {
      return null;
    }
    const from = days.findIndex((day) => day >= windowDays.from);
    let to = -1;
    days.forEach((day, index) => {
      if (day <= windowDays.to) {
        to = index;
      }
    });
    if (from < 0 || to < from) {
      return null;
    }
    const left = span(from)[0];
    const right = span(to)[1];
    return (
      <g data-testid="topic-timeline-brush" pointerEvents="none">
        <rect
          x={PAD_LEFT}
          y={PAD_TOP}
          width={Math.max(0, left - PAD_LEFT)}
          height={plotBottom - PAD_TOP}
          fill="hsl(var(--background) / 0.6)"
        />
        <rect
          x={right}
          y={PAD_TOP}
          width={Math.max(0, width - right)}
          height={plotBottom - PAD_TOP}
          fill="hsl(var(--background) / 0.6)"
        />
        {brushBand(from, to, "topic-timeline-brush-band")}
      </g>
    );
  };

  const renderPlot = (rows: Array<SocialFilledBucket>) => {
    const xTickStep = Math.max(1, Math.round(rows.length / (width > NARROW_WIDTH ? 7 : 3)));
    const xTicks = rows.map((_, index) => index).filter((index) => index % xTickStep === 0);
    const hovered = hoverIndex != null && hoverIndex < rows.length ? hoverIndex : null;
    return (
      <svg
        role="img"
        aria-label={t(`socialListening.topicPage.timeline.chartAria.${metric}`)}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="block overflow-visible"
      >
        <g data-testid="topic-timeline-grid">
          {[0, 0.5, 1].map((fraction) => (
            <line
              key={fraction}
              x1={PAD_LEFT}
              x2={width}
              y1={y(axisMax * fraction)}
              y2={y(axisMax * fraction)}
              stroke="hsl(var(--card-border))"
              strokeWidth={1}
            />
          ))}
        </g>
        <g className="fill-muted-foreground text-[11px]">
          {[0, 0.5, 1].map((fraction) => (
            <text
              key={fraction}
              x={PAD_LEFT - 8}
              y={y(axisMax * fraction) + 4}
              textAnchor="end"
              className="tabular-nums"
            >
              {formatCount(Math.round(axisMax * fraction))}
            </text>
          ))}
          {xTicks.map((index) => (
            <text key={rows[index].start} x={x(index)} y={plotBottom + 16} textAnchor="middle">
              {dates[index]}
            </text>
          ))}
          {hasMood ? (
            <text x={PAD_LEFT - 6} y={height - 1} textAnchor="end">
              {t("socialListening.topicPage.timeline.mood")}
            </text>
          ) : null}
        </g>
        {isBrandSplit
          ? shownLines.map((line) =>
              n > 1 ? (
                <path
                  key={line.brandId}
                  data-testid={`topic-timeline-brand-line-${line.brandId}`}
                  data-values={line.values.join(",")}
                  d={monotonePath(line.values.map((value, index) => [x(index), y(value)]))}
                  fill="none"
                  stroke={brandColor(line.brandId)}
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              ) : (
                <circle
                  key={line.brandId}
                  data-testid={`topic-timeline-brand-line-${line.brandId}`}
                  data-values={line.values.join(",")}
                  cx={x(0)}
                  cy={y(line.values[0] ?? 0)}
                  r={4}
                  fill={brandColor(line.brandId)}
                />
              ),
            )
          : null}
        {!isBrandSplit && n > 1
          ? stack.layers.map((layer, rank) => (
              <path
                key={layer.platform}
                data-testid={`topic-timeline-area-${layer.platform}`}
                data-values={layer.points.map((point) => point.value).join(",")}
                d={bandPath(
                  layer.points.map((point, index) => [x(index), y(point.y1)]),
                  layer.points.map((point, index) => [x(index), y(point.y0)]),
                )}
                fill={seriesColors[rank]}
                fillOpacity={TIMELINE_AREA_OPACITY}
              />
            ))
          : null}
        {isBrandSplit ? null : n > 1 ? (
          <path
            data-testid="topic-timeline-total"
            data-values={stack.totals.join(",")}
            d={monotonePath(stack.totals.map((value, index) => [x(index), y(value)]))}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            strokeLinecap="round"
          />
        ) : (
          <circle
            data-testid="topic-timeline-total"
            data-values={stack.totals.join(",")}
            cx={x(0)}
            cy={y(stack.totals[0] ?? 0)}
            r={4}
            fill="hsl(var(--primary))"
          />
        )}
        <g data-testid="topic-timeline-ribbon">
          {(hasMood ? nets : []).map((net, index) => {
            const [left, right] = span(index);
            return (
              <rect
                key={rows[index].start}
                data-testid={`topic-timeline-mood-${index}`}
                data-net={net == null ? "" : String(net)}
                x={left + 1}
                y={height - RIBBON_FROM_BOTTOM}
                width={Math.max(1, right - left - 2)}
                height={RIBBON_HEIGHT}
                rx={2}
                fill={moodFill(net)}
              />
            );
          })}
        </g>
        {renderBrush()}
        {hovered != null ? (
          <g pointerEvents="none" data-testid="topic-timeline-guide">
            <line
              x1={x(hovered)}
              x2={x(hovered)}
              y1={PAD_TOP}
              y2={plotBottom}
              stroke="hsl(var(--foreground) / 0.2)"
            />
            {isBrandSplit ? (
              shownLines.map((line) => (
                <circle
                  key={line.brandId}
                  cx={x(hovered)}
                  cy={y(line.values[hovered] ?? 0)}
                  r={3.5}
                  fill="hsl(var(--background))"
                  stroke={brandColor(line.brandId)}
                  strokeWidth={2}
                />
              ))
            ) : (
              <circle
                cx={x(hovered)}
                cy={y(stack.totals[hovered])}
                r={4.5}
                fill="hsl(var(--background))"
                stroke="hsl(var(--primary))"
                strokeWidth={2.5}
              />
            )}
          </g>
        ) : null}
        <rect
          data-testid="topic-timeline-hit"
          x={PAD_LEFT}
          y={0}
          width={width - PAD_LEFT}
          height={height}
          fill="transparent"
          className="cursor-crosshair"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
        />
        {peaks.map((peak) => {
          const px = x(peak.index);
          // Brand lines share no total: the marker sits on the day's highest shown line.
          const py = y(isBrandSplit ? shownMaxAt(peak.index) : peak.value);
          const day = rows[peak.index].start;
          const marker = (
            <g
              data-testid={`topic-timeline-peak-${peak.letter}`}
              data-day={day}
              role="button"
              tabIndex={0}
              aria-label={t("socialListening.topicPage.peak.aria", {
                letter: peak.letter,
                date: dates[peak.index],
              })}
              className="group cursor-pointer outline-none"
              onPointerEnter={() => onHover(null)}
              onKeyDown={(event) => onPeakKeyDown(event, peak.letter)}
            >
              <line x1={px} x2={px} y1={py - 4} y2={py - 14} stroke="hsl(var(--foreground) / 0.45)" />
              <circle
                cx={px}
                cy={py - 25}
                r={11}
                fill="hsl(var(--foreground))"
                className="origin-center transition-transform duration-150 [transform-box:fill-box] group-hover:scale-110 group-focus-visible:stroke-ring group-focus-visible:[stroke-width:3px]"
              />
              <text
                x={px}
                y={py - 21}
                textAnchor="middle"
                fill="hsl(var(--background))"
                className="pointer-events-none text-[11px] font-bold"
              >
                {peak.letter}
              </text>
            </g>
          );
          return (
            <SocialPeakPopover
              key={peak.letter}
              context={context}
              letter={peak.letter}
              day={day}
              value={peak.value}
              multipleOfAverage={peak.multipleOfAverage}
              isOpen={openPeak === peak.letter}
              onOpenChange={(isOpen) => setOpenPeak(isOpen ? peak.letter : null)}
              trigger={marker}
              seriesSplit={seriesSplit}
            />
          );
        })}
      </svg>
    );
  };

  const channelTooltipRows = (index: number): Array<SocialChartTooltipRow> =>
    stack.layers
      .filter((layer) => layer.points[index].value > 0)
      .map((layer) => ({
        key: layer.platform,
        label: SOCIAL_PLATFORM_LABELS[layer.platform],
        platform: layer.platform,
        value: formatCount(layer.points[index].value),
        color: seriesColors[stack.order.indexOf(layer.platform)],
      }));

  const brandTooltipRows = (index: number): Array<SocialChartTooltipRow> =>
    shownLines
      .map((line) => ({ line, value: line.values[index] ?? 0 }))
      .sort((a, b) => b.value - a.value)
      .map(({ line, value }) => ({
        key: line.brandId,
        label: brandName(line.brandId),
        value: formatCount(value),
        color: brandColor(line.brandId),
      }));

  const hovered = hoverIndex != null && hoverIndex < n ? hoverIndex : null;
  const tooltip =
    hovered != null ? (
      <SocialChartTooltip
        testId="topic-timeline-tooltip"
        title={[
          dates[hovered],
          // Brand lines share no total: the Topic-wide figures would not match the rows below.
          ...(isBrandSplit ? [] : [valueLabel(stack.totals[hovered])]),
          // In-period comments that day (D23): activity beside the posts, never stacked into them.
          ...(!isBrandSplit && buckets[hovered].total.comments > 0
            ? [
                t("socialListening.detail.engagement.comments", {
                  count: buckets[hovered].total.comments,
                  value: formatCount(buckets[hovered].total.comments),
                }),
              ]
            : []),
        ].join(", ")}
        rows={isBrandSplit ? brandTooltipRows(hovered) : channelTooltipRows(hovered)}
        footer={
          nets[hovered] != null
            ? {
                label: t("socialListening.topicPage.timeline.netSentiment"),
                value: signed(nets[hovered] as number),
              }
            : undefined
        }
        anchorX={x(hovered)}
        containerWidth={width}
      />
    ) : null;

  const isEmpty = n === 0;

  return (
    <section
      data-testid="topic-over-time"
      aria-labelledby="topic-timeline-title"
      className="min-w-0 rounded-[calc(var(--radius)+8px)] border border-card-border bg-background"
    >
      <div className="flex min-h-9 flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 pt-4">
        <h2
          id="topic-timeline-title"
          className="m-0 font-sans text-sm font-medium"
          title={metric === "reach" ? t("socialListening.topicPage.timeline.caption") : undefined}
        >
          {title}
        </h2>
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
          {isEmpty ? null : (
            <span className="text-xs text-muted-foreground" data-testid="topic-timeline-hint">
              {hint}
            </span>
          )}
          {hasBrands ? (
            <SocialMeasureSwitch
              options={TIMELINE_SPLITS.map((option) => ({
                value: option,
                label: t(`socialListening.topicPage.timeline.split.${option}`),
              }))}
              value={split}
              onChange={setSplit}
              ariaLabel={t("socialListening.topicPage.timeline.splitAria")}
              testIdPrefix="topic-timeline-split"
            />
          ) : null}
        </div>
      </div>
      {isEmpty ? null : legend}
      <div className={`relative touch-pan-y px-5 pt-1 ${SOCIAL_PANEL_BOTTOM_PADDING}`}>
        <div ref={plotRef} className="relative w-full">
          {isEmpty ? (
            <div
              data-testid="topic-timeline-empty"
              aria-busy={isLoading}
              style={{ height }}
              className="w-full"
            />
          ) : (
            <>
              {renderPlot(buckets)}
              {drag ? null : tooltip}
              <ul
                aria-label={t(`socialListening.topicPage.timeline.chartAria.${metric}`)}
                className="sr-only"
              >
                {buckets.map((entry, index) => (
                  <li
                    key={entry.start}
                    tabIndex={0}
                    data-testid={`topic-timeline-day-${index}`}
                    onFocus={() => onHover(index)}
                    onBlur={() => onHover(null)}
                    onKeyDown={(event) => onDayKeyDown(event, index)}
                  >
                    {[
                      dates[index],
                      ...(isBrandSplit ? [] : [valueLabel(stack.totals[index])]),
                      ...(isBrandSplit
                        ? shownLines.map(
                            (line) =>
                              `${brandName(line.brandId)} ${formatCount(line.values[index] ?? 0)}`,
                          )
                        : stack.layers.map(
                            (layer) =>
                              `${SOCIAL_PLATFORM_LABELS[layer.platform]} ${formatCount(layer.points[index].value)}`,
                          )),
                      ...(nets[index] != null
                        ? [
                            `${t("socialListening.topicPage.timeline.netSentiment")} ${signed(nets[index] as number)}`,
                          ]
                        : []),
                    ].join(", ")}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
